import { useIntl } from 'react-intl';
import { Url } from '@rossum/api-client';
import { IconAlertOctagon } from '@rossum/ui/icons/tabler';
import {
  Stack,
  SvgIcon,
  tablePaginationClasses,
  Theme,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import {
  DataGridPro,
  gridClasses,
  GridRowSelectionModel,
} from '@rossum/ui/x-data-grid-pro';
import useDebounce from '../../../utils/hooks/useDebounce';
import {
  SimpleColumn,
  useSimpleColumns,
} from '../../document-list-columns/hooks/useSimpleColumns';
import {
  RelationCandidate,
  searchRelationCandidatesPagination,
  useSearchRelationCandidates,
} from '../hooks/useSearchRelationCandidates';
import { GridSkeleton } from './GridSkeleton';

const ROW_HEIGHT = 48;

// Grid columns are typed for TransformedAnnotation, hence the narrowing.
const isInSameSubtypeRelation = (row: unknown): boolean =>
  typeof row === 'object' &&
  row !== null &&
  'isInSameSubtypeRelation' in row &&
  row.isInSameSubtypeRelation === true;

const dataGridStyles = {
  borderColor: 'transparent',
  [`& .${gridClasses.columnHeader}`]: {
    color: 'text.secondary',
  },
  [`& .${tablePaginationClasses.displayedRows}`]: {
    m: 0,
  },
  [`& .${gridClasses.cell}, & .${gridClasses.columnHeaders}, & .${gridClasses.footerContainer}`]:
    {
      borderColor: 'transparent',
    },
  [`.${gridClasses.row}`]: {
    backgroundColor: (t: Theme) => t.palette.background.paper,
    mb: 0.5,
  },
};

type CreateRelationGridProps = {
  searchValue: string;
  subtypeName: string;
  rootAnnotationUrl: Url;
  excludedAnnotations: Url[];
  defaultQueueUrl: Url | null;
  rowSelectionModel: GridRowSelectionModel;
  onRowSelectionModelChange: (
    candidates: RelationCandidate[],
    newModel: GridRowSelectionModel
  ) => void;
};

export const CreateRelationGrid = ({
  searchValue,
  subtypeName,
  rootAnnotationUrl,
  excludedAnnotations,
  defaultQueueUrl,
  rowSelectionModel,
  onRowSelectionModelChange,
}: CreateRelationGridProps) => {
  const intl = useIntl();

  const debouncedSearchValue = useDebounce(searchValue, 350);

  const {
    candidates,
    isLoading,
    isError,
    paginationContext: {
      rowCount,
      paginationModel,
      handlePaginationModelChange,
    },
  } = useSearchRelationCandidates({
    searchValue: debouncedSearchValue,
    subtypeName,
    rootAnnotationUrl,
    excludedAnnotations,
    defaultQueueUrl,
  });

  const columns = useSimpleColumns({
    columnsLocation: 'customRelationsModal',
  }).map(column =>
    column.field === 'original_file_name'
      ? ({
          ...column,
          renderCell: props => (
            <Stack direction="row" spacing={1} alignItems="center" height={1}>
              {isInSameSubtypeRelation(props.row) ? (
                <Tooltip
                  title={intl.formatMessage({
                    id: 'features.customRelations.createDialog.disabledAnnotationTooltip',
                  })}
                >
                  <SvgIcon color="warning" fontSize="medium">
                    <IconAlertOctagon />
                  </SvgIcon>
                </Tooltip>
              ) : null}
              {column.renderCell?.(props)}
            </Stack>
          ),
        } satisfies SimpleColumn)
      : column
  );

  return (
    <>
      <Typography variant="body1" sx={{ my: 0.5, whiteSpace: 'pre-wrap' }}>
        {searchValue
          ? intl.formatMessage(
              { id: 'features.customRelations.createDialog.resultsFor' },
              { searchQuery: searchValue }
            )
          : defaultQueueUrl
            ? intl.formatMessage({
                id: 'features.customRelations.createDialog.recentFromQueue',
              })
            : ' '}
      </Typography>
      <Stack flex={1} minHeight={0}>
        <DataGridPro
          disableRowSelectionExcludeModel
          loading={isLoading}
          columns={columns}
          rows={isError ? [] : candidates}
          rowSelectionModel={rowSelectionModel}
          rowHeight={ROW_HEIGHT}
          onRowSelectionModelChange={newModel =>
            onRowSelectionModelChange(candidates, newModel)
          }
          isRowSelectable={params => !isInSameSubtypeRelation(params.row)}
          checkboxSelection
          disableColumnMenu
          disableColumnResize
          disableRowSelectionOnClick
          keepNonExistentRowsSelected
          hideFooterSelectedRowCount
          pageSizeOptions={[searchRelationCandidatesPagination.pageSize]}
          rowCount={rowCount}
          pagination
          paginationMode="server"
          onPaginationModelChange={handlePaginationModelChange}
          paginationModel={paginationModel}
          slots={{
            loadingOverlay: () => <GridSkeleton isLoading={isLoading} />,
            noRowsOverlay: () => (
              <Stack justifyContent="center" alignItems="center" height={1}>
                <Typography variant="body2">
                  {intl.formatMessage({
                    id: isError
                      ? ('features.customRelations.createDialog.genericError' as const)
                      : !searchValue && !defaultQueueUrl
                        ? ('features.customRelations.createDialog.searchHint' as const)
                        : ('features.customRelations.createDialog.noResults' as const),
                  })}
                </Typography>
              </Stack>
            ),
          }}
          sx={dataGridStyles}
        />
      </Stack>
    </>
  );
};
